import { LoadingButton } from '@mui/lab'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { Box, Button, Card, Chip, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { CustomerDto } from '../../../interactors/gen/backendClient'
import { colors, theme } from '../../theme'
import { ChoosePaymentPlanModal } from './ChoosePaymentPlanModal'

interface Props {
  customer: CustomerDto
  isSubscribed: boolean
  isManageLoading: boolean
  currentUserIsAdmin: boolean
  onManageSubscription: () => void
}

type OfferName = 'free' | 'essential' | 'premium'

type OfferDraft = {
  key: OfferName
  title: string
  price: string
  annualPrice?: string
  annualDiscountLabel?: string
  benefit: string
  description: string
  features: { text: string; muted?: boolean }[]
}

type OfferCheckoutConfig = {
  offerName: string
  prices: [string, string]
  priceIds: [string, string]
}

const offerDrafts: OfferDraft[] = [
  {
    key: 'free',
    title: 'Offre gratuite',
    price: 'Gratuit',
    benefit: 'Publiez vos annonces, favorisez l’adoption',
    description: "Rendez vos animaux visibles et disponibles à l'adoption.",
    features: [
      { text: 'Fiches animales et suivi de prise en charge' },
      { text: 'Publication sur Petso et sites partenaires' },
      { text: 'Mise à jour et synchronisation en temps réel' },
      { text: 'Suivi de santé et rappels : 3 actes de santé par animal*' },
      { text: 'Documents : 1 document par fiche animale*' },
      { text: 'Utilisateurs illimités' },
      { text: "Suivi des statuts et des procédures d'adoption" },
    ],
  },
  {
    key: 'essential',
    title: 'Essentielle',
    price: '18€/mois TTC',
    annualPrice: '180€/an',
    annualDiscountLabel: '-15%',
    benefit: 'Pour le quotidien : suivi santé, rappels et mode collaboratif',
    description: 'Suivi et historique de santé, notifications de rappel et gestion de documents.',
    features: [
      { text: 'Fiches animales et suivi de prise en charge', muted: true },
      { text: 'Publication sur Petso et sites partenaires', muted: true },
      { text: 'Mise à jour et synchronisation en temps réel', muted: true },
      { text: 'Historique de santé, documents et rappels illimités' },
      { text: 'Utilisateurs illimités', muted: true },
      { text: 'Tableau de bord' },
      { text: "Suivi des statuts et des procédures d'adoption", muted: true },
    ],
  },
  {
    key: 'premium',
    title: 'Utile',
    price: '48€/mois TTC',
    annualPrice: '492€/an',
    annualDiscountLabel: '-15%',
    benefit: 'Gagnez du temps dans votre gestion des adoptants',
    description: 'Automatisez vos procédures d’adoption et vos tâches administratives.',
    features: [
      { text: "Tout ce qui est inclus dans l'offre Essentielle", muted: true },
      { text: 'Suivi des déplacements' },
      { text: 'Gestion avancée des boxes' },
      { text: "Création de modèles personnalisés de documents à l'adoption" },
      { text: 'Transmission en un clic et signature électronique' },
    ],
  },
]

const currentPlanLabel = (customer: CustomerDto, isSubscribed: boolean) => {
  if (!isSubscribed) return 'free'
  return customer.subscription?.plan === 'premium'
    ? 'premium'
    : customer.subscription?.plan === 'essential'
    ? 'essential'
    : 'free'
}

const benefitColorByOffer: Record<OfferName, string> = {
  free: '#EAF1FA',
  essential: '#FFF4D9',
  premium: '#E6F5EB',
}

const offerCheckoutConfigByKey: Record<Exclude<OfferName, 'free'>, OfferCheckoutConfig> = {
  essential: {
    offerName: 'essentielle',
    prices: ['18', '150'],
    priceIds: ['price_1O4JLQKwIFnHkxNPsRR2QmjR', 'price_1O4JMFKwIFnHkxNPuoJnMf8S'],
  },
  premium: {
    offerName: 'utile',
    prices: ['48', '492'],
    priceIds: ['price_1O4JKCKwIFnHkxNPL6rDRzL5', 'price_1O4JJlKwIFnHkxNPQY9EHNDk'],
  },
}

export const TemporaryOffersDraft: React.FC<Props> = ({
  customer,
  isSubscribed,
  isManageLoading,
  currentUserIsAdmin,
  onManageSubscription,
}) => {
  const activePlan = currentPlanLabel(customer, isSubscribed)
  const hasCanceledSubscription = customer?.subscription?.status === 'canceled'

  const renderAction = (offer: OfferDraft) => {
    const isCurrentOffer = activePlan === offer.key
    const isIncludedInPremium = activePlan === 'premium' && offer.key === 'essential'

    if (offer.key === 'free') {
      return (
        <Button
          variant="outlined"
          disabled
          sx={{
            width: '100%',
            maxWidth: 280,
            alignSelf: 'stretch',
            borderRadius: '999px',
            fontWeight: 700,
            fontSize: 15,
            textTransform: 'none',
          }}
        >
          {isCurrentOffer ? 'Offre actuelle' : 'Toujours incluse'}
        </Button>
      )
    }

    if (isCurrentOffer) {
      return (
        <Tooltip title={!currentUserIsAdmin ? "Seuls les administrateurs peuvent gérer l'abonnement" : ''}>
          <span style={{ width: '100%' }}>
            <LoadingButton
              onClick={onManageSubscription}
              variant="contained"
              loading={isManageLoading}
              disabled={!currentUserIsAdmin}
              sx={{
                width: '100%',
                borderRadius: '999px',
                fontWeight: 700,
                fontSize: 15,
                textTransform: 'none',
                backgroundColor: '#EEF2F8',
                color: colors.cello,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#E3EAF5',
                  boxShadow: 'none',
                },
              }}
            >
              {hasCanceledSubscription ? 'Renouveler mon offre' : 'Gérer mon abonnement'}
            </LoadingButton>
          </span>
        </Tooltip>
      )
    }

    if (isIncludedInPremium) {
      return (
        <Button
          variant="outlined"
          disabled
          sx={{
            width: '100%',
            borderRadius: '999px',
            fontWeight: 700,
            fontSize: 15,
            textTransform: 'none',
          }}
        >
          Déjà inclus dans Utile
        </Button>
      )
    }

    const checkoutConfig = offerCheckoutConfigByKey[offer.key]

    return (
      <ChoosePaymentPlanModal
        offerName={checkoutConfig.offerName}
        prices={checkoutConfig.prices}
        priceIds={checkoutConfig.priceIds}
        disabled={!currentUserIsAdmin}
      >
        {({ openModal }) => (
          <Tooltip title={!currentUserIsAdmin ? "Seuls les administrateurs peuvent gérer l'abonnement" : ''}>
            <span style={{ width: '100%' }}>
              <Button
                onClick={openModal}
                variant={offer.key === 'premium' ? 'contained' : 'outlined'}
                color="secondary"
                disabled={!currentUserIsAdmin}
                sx={{
                  width: '100%',
                  borderRadius: '999px',
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: 'none',
                  boxShadow: 'none',
                }}
              >
                Choisir cette offre
              </Button>
            </span>
          </Tooltip>
        )}
      </ChoosePaymentPlanModal>
    )
  }

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 1.5,
        }}
      >
        {offerDrafts.map((offer) => {
          const isCurrentOffer = activePlan === offer.key

          return (
            <Card
              key={offer.key}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100%',
                border: isCurrentOffer ? `1px solid ${theme.palette.secondary.main}` : '1px solid #E4E8F2',
                boxShadow: isCurrentOffer ? '0 10px 24px rgba(32, 56, 100, 0.08)' : undefined,
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, md: 2.15 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 19, fontWeight: 800, color: colors.cello }}>{offer.title}</Typography>
                    <Typography sx={{ mt: 0.35, fontSize: 24, fontWeight: 800, color: colors.cello }}>
                      {offer.price}
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.6,
                        minHeight: 30,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexWrap: 'wrap',
                      }}
                    >
                      {offer.annualPrice && (
                        <>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#7A8197' }}>
                            ou {offer.annualPrice}
                          </Typography>
                          <Chip
                            label={offer.annualDiscountLabel}
                            size="small"
                            sx={{
                              height: 22,
                              backgroundColor: '#E6F5EB',
                              color: '#2F6B47',
                              fontWeight: 700,
                            }}
                          />
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {!isCurrentOffer && offer.key === 'essential' && (
                      <Chip
                        label="Populaire"
                        sx={{
                          backgroundColor: '#FFF4D9',
                          color: colors.cello,
                          fontWeight: 700,
                        }}
                      />
                    )}
                    {isCurrentOffer && (
                      <Chip
                        label="Offre active"
                        sx={{
                          backgroundColor: '#EAF1FA',
                          color: colors.cello,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 1.3,
                    borderRadius: '16px',
                    backgroundColor: benefitColorByOffer[offer.key],
                    border: '1px solid rgba(32, 56, 100, 0.08)',
                  }}
                >
                  <Typography sx={{ fontSize: 17, fontWeight: 800, color: colors.cello }}>{offer.benefit}</Typography>
                  <Typography sx={{ mt: 0.65, fontSize: 14, lineHeight: 1.38, color: colors.cello }}>
                    {offer.description}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.95, flex: 1 }}>
                  {offer.features.map((feature) => (
                    <Box
                      key={`${offer.key}-${feature.text}`}
                      sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                    >
                      <CheckRoundedIcon
                        sx={{
                          mt: '2px',
                          fontSize: 18,
                          color: feature.muted ? '#A8B5C8' : theme.palette.success.main,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 14,
                          lineHeight: 1.4,
                          color: feature.muted ? '#7A8197' : colors.cello,
                          fontWeight: feature.muted ? 500 : 600,
                        }}
                      >
                        {feature.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ pt: 0.5, display: 'flex', alignItems: 'center', mt: 'auto' }}>{renderAction(offer)}</Box>
              </Box>
            </Card>
          )
        })}
      </Box>

      <Typography
        sx={{
          mt: 1.5,
          fontSize: 12.5,
          lineHeight: 1.35,
          color: colors.bermudaGray,
          fontStyle: 'italic',
          textAlign: 'left',
        }}
      >
        * 3 mois sans limite sur les documents et le suivi santé pour les nouvelles associations, puis 1 document et 3
        actes de santé par animal. L’illimité peut être conservé avec Stripe Connect.
      </Typography>
    </Box>
  )
}
